/**
 * Content Section Transformers
 * Aggregates all content-related transformers and provides section-level utilities
 */

import { transformTitleTag } from './titleTagTransformer';
import { transformMetaDescription } from './metaDescriptionTransformer';
import { transformHeadings } from './headingsTransformer';
import { transformContentAnalysis } from './contentAnalysisTransformer';
import { transformAltAttribute } from './altAttributeTransformer';
import { transformInPageLinks } from './inPageLinksTransformer';
import { transformPhoneNumbers } from './phoneNumbersTransformer';
import { transformWebFeeds } from './webFeedsTransformer';
import { transformGooglePreview } from './googlePreviewTransformer';

// Re-export individual transformers
export { transformTitleTag } from './titleTagTransformer';
export { transformMetaDescription } from './metaDescriptionTransformer';
export { transformHeadings } from './headingsTransformer';
export { transformContentAnalysis } from './contentAnalysisTransformer';
export { transformAltAttribute } from './altAttributeTransformer';
export { transformInPageLinks } from './inPageLinksTransformer';
export { transformPhoneNumbers } from './phoneNumbersTransformer';
export { transformWebFeeds } from './webFeedsTransformer';
export { transformGooglePreview } from './googlePreviewTransformer';

/**
 * Transforms all content section criteria from WooRank API response
 * @param {Object} apiData - The full API response data
 * @returns {Object} - Transformed content section data with aggregated counts
 */
export const transformContentSection = (apiData = {}) => {
  const criteria = apiData?.criteria || {};
  const url = apiData?.url || '';

  // Transform individual criteria
  const titleTag = transformTitleTag(criteria?.title, url);
  const metaDescription = transformMetaDescription(criteria?.description, url);
  const headings = transformHeadings(criteria?.headings);
  const contentAnalysis = transformContentAnalysis(criteria?.keywords_cloud);
  const altAttribute = transformAltAttribute(criteria?.images);
  const inPageLinks = transformInPageLinks(criteria?.links_details);
  const phoneNumbers = transformPhoneNumbers(criteria?.phone_numbers);
  const webFeeds = transformWebFeeds(criteria?.web_feeds);
  const googlePreview = transformGooglePreview(criteria?.google_preview);

  // Aggregate all results for counting
  const allResults = [titleTag, metaDescription, headings, contentAnalysis, altAttribute, inPageLinks, phoneNumbers, webFeeds, googlePreview];

  // Calculate error and improvement counts
  const errorCount = allResults.filter((result) => result?.status === 'error').length || 0;
  const toImproveCount = allResults.filter((result) => result?.status === 'warning').length || 0;

  return {
    errorCount,
    toImproveCount,
    titleTag,
    metaDescription,
    headings,
    contentAnalysis,
    altAttribute,
    inPageLinks,
    phoneNumbers,
    webFeeds,
    googlePreview,
  };
};

export default transformContentSection;

